import { Button, Input, Select } from "components"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useTaskList } from "pages/Task/utils/hooks"

type TaskViewMode = "list" | "board"

export type TaskSummaryStats = {
  total: number
  urgentHigh: number
  completed: number
  avgProgress: number
}

type TaskListChromeProps = {
  tasks: ReturnType<typeof useTaskList>
  summaryStats: TaskSummaryStats
  viewMode: TaskViewMode
  onViewModeChange: (mode: TaskViewMode) => void
  statusOptions: { value: string; label: string }[]
  statusValue: string | undefined
  onStatusChange: (value: string) => void
  departmentValue: string
  onDepartmentChange: (value: string) => void
  searchValue: string
  onSearchChange: (value: string) => void
  onCreateTask: () => void
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  /** Format: THU 30 APR 2026 08:51:11 */
  const d = now
  const formatted = `${d
    .toLocaleDateString("en-GB", { weekday: "short" })
    .toUpperCase()} ${String(d.getDate()).padStart(2, "0")} ${d
    .toLocaleDateString("en-GB", { month: "short" })
    .toUpperCase()} ${d.getFullYear()} ${d.toLocaleTimeString("en-GB", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  })}`

  return (
    <time
      dateTime={now.toISOString()}
      className="text-[13px] font-semibold whitespace-nowrap tracking-wide text-[#f6b800]">
      {formatted}
    </time>
  )
}

export function TaskListChrome({
  tasks,
  summaryStats,
  viewMode,
  onViewModeChange,
  statusOptions,
  statusValue,
  onStatusChange,
  departmentValue,
  onDepartmentChange,
  searchValue,
  onSearchChange,
  onCreateTask
}: TaskListChromeProps) {
  const departmentOptions = useMemo(() => {
    const map = new Map<number, string>()
    for (const t of tasks.data ?? []) {
      if (t.departement_id != null) {
        map.set(t.departement_id, t.department_name ?? "")
      }
    }
    const rest = Array.from(map.entries())
      .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""))
      .map(([id, name]) => ({
        value: String(id),
        label: name || `Dept ${id}`
      }))
    return [{ value: "all", label: "All Department" }, ...rest]
  }, [tasks.data])

  /** Status & department: same width as each other (match department filter). */
  const filterSelectClass = "w-full sm:w-[260px] sm:max-w-[280px] shrink-0"

  return (
    <div className="w-full mb-4 space-y-0 border border-base-300 rounded-lg overflow-hidden bg-base-100 shadow-sm">
      {/* Top bar — mock: black strip */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-black text-white px-4 py-3.5">
        <h1 className="text-lg font-bold uppercase tracking-wide m-0 font-sans">
          TASK MANAGEMENT
        </h1>
        <LiveClock />
      </div>

      {/* Toolbar — inline: status | department (same width) | search | actions */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-base-100 border-b border-base-300">
        <div className={filterSelectClass}>
          <Select
            className="w-full"
            controlClass="!h-10 !min-h-[2.5rem] !py-0"
            placeholder="All Status"
            options={statusOptions}
            value={
              statusValue
                ? statusOptions.find((o) => o.value === statusValue) ?? null
                : null
            }
            onChange={(e) => {
              if (e?.value != null) onStatusChange(String(e.value))
            }}
          />
        </div>

        <div className={filterSelectClass}>
          <Select
            className="w-full"
            controlClass="!h-10 !min-h-[2.5rem] !py-0"
            placeholder="All Department"
            options={departmentOptions}
            value={
              departmentOptions.find((o) => o.value === departmentValue) ??
              departmentOptions[0]
            }
            onChange={(e) => {
              if (e?.value != null) onDepartmentChange(String(e.value))
            }}
          />
        </div>

        <div className="w-full min-w-0 sm:flex-1 sm:min-w-[180px]">
          <Input
            className="w-full"
            placeholder="Search task, owner, department..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            inputClass="h-10 rounded-md border-[#d9d9d9]"
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 w-full sm:w-auto sm:ml-auto">
          <button
            type="button"
            className="h-10 min-h-10 px-3 rounded-md border border-[#d9d9d9] bg-white text-xs font-bold uppercase text-black hover:bg-base-200"
            onClick={() =>
              toast("Calendar view — coming soon", { icon: "📅" })
            }>
            Calendar
          </button>
          <button
            type="button"
            className={`h-10 min-h-10 px-3 rounded-md border border-[#d9d9d9] text-xs font-bold uppercase ${
              viewMode === "list"
                ? "bg-base-200 text-black"
                : "bg-white text-black hover:bg-base-200"
            }`}
            onClick={() => onViewModeChange("list")}>
            Table
          </button>
          <button
            type="button"
            className={`h-10 min-h-10 px-3 rounded-md border border-[#d9d9d9] text-xs font-bold uppercase ${
              viewMode === "board"
                ? "bg-base-200 text-black"
                : "bg-white text-black hover:bg-base-200"
            }`}
            onClick={() => onViewModeChange("board")}>
            Board
          </button>
          <Button
            className="h-10 min-h-10 rounded-md border-0 bg-primary px-4 text-xs font-bold uppercase text-black hover:brightness-95"
            onClick={onCreateTask}>
            Create New Task
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-base-100">
        <SummaryCard
          value={String(summaryStats.total)}
          label="Total Tasks"
        />
        <SummaryCard
          value={String(summaryStats.urgentHigh)}
          label="Urgent / High"
        />
        <SummaryCard
          value={String(summaryStats.completed)}
          label="Completed"
        />
        <SummaryCard
          value={`${summaryStats.avgProgress}%`}
          label="Average Progress"
        />
      </div>
    </div>
  )
}

function SummaryCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-[10px] border border-base-300 bg-base-100 p-3">
      <strong className="block text-lg font-bold leading-tight">{value}</strong>
      <span className="text-[11px] font-bold uppercase text-base-content/50 tracking-wide">
        {label}
      </span>
    </div>
  )
}
